import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { FileText, Image as ImageIcon, ExternalLink, Download, Search, Upload, Loader2, Pencil, Trash2, MessageSquare, Tag } from 'lucide-react';
import PresentationsButton from './PresentationsButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  'floor-plan': '#1F4FBE',
  structural: '#EF4444',
  electrical: '#EAB308',
  plumbing: '#3B82F6',
  hvac: '#8B5CF6',
  landscaping: '#22C55E',
  mep: '#FF6F59',
  general: '#7A8C8C',
};

const CATEGORY_LABELS = {
  'floor-plan': 'Floor Plan',
  structural: 'Structural',
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  hvac: 'HVAC',
  landscaping: 'Landscaping',
  mep: 'MEP',
  general: 'General',
};

function fileExt(name) { return (name?.split('.').pop() || '').toLowerCase(); }
function isImage(ext) { return ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'].includes(ext); }
function isPdf(ext) { return ext === 'pdf'; }
// DXF files get a sibling .jpg thumbnail generated server-side at upload time.
function thumbnailForCad(fileUrl) {
  if (!fileUrl) return null;
  const ext = fileExt(fileUrl);
  if (ext !== 'dxf') return null;
  return fileUrl.replace(/\.dxf(\?.*)?$/i, '.jpg');
}

export default function TechnicalPlansDialog({ open, onClose }) {
  const [plans, setPlans] = useState([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;

  const loadPlans = () => {
    axios.get(`${API}/technical-plans`).then(({ data }) => setPlans(data)).catch(() => {});
  };

  useEffect(() => {
    if (!open) return;
    loadPlans();
  }, [open]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!adminToken) { alert('You must be signed in as admin to upload plans.'); return; }
    // iter-122 — Multi-file plan upload. Same UX as TechnicalPlansManager:
    // single file = full prompts; batch = category once + filename titles.
    let batchCategory = null;
    let promptedTitle = null;
    if (files.length === 1) {
      promptedTitle = window.prompt('Plan title:', files[0].name.replace(/\.[^.]+$/, ''));
      if (!promptedTitle) { if (fileRef.current) fileRef.current.value = ''; return; }
      const categoryKeys = Object.keys(CATEGORY_LABELS);
      batchCategory = window.prompt(`Category (${categoryKeys.join(', ')}):`, 'floor-plan') || 'general';
    } else {
      const categoryKeys = Object.keys(CATEGORY_LABELS);
      batchCategory = window.prompt(
        `Uploading ${files.length} plans. Category for all (${categoryKeys.join(', ')}):`,
        'floor-plan',
      );
      if (!batchCategory) { if (fileRef.current) fileRef.current.value = ''; return; }
    }
    setUploading(true);
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('keep_raw', 'true');
        // eslint-disable-next-line no-await-in-loop
        const { data: upload } = await axios.post(`${API}/admin/upload`, fd, {
          headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
        const extName = (file.name.split('.').pop() || '').toLowerCase();
        const titleForFile = promptedTitle || file.name.replace(/\.[^.]+$/, '');
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API}/admin/technical-plans`, {
          title: titleForFile,
          category: batchCategory,
          file_url: `${BACKEND}${upload.url}`,
          file_name: file.name,
          file_type: extName,
          order: plans.length + i,
        }, { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true });
        ok += 1;
      } catch (err) {
        console.error('Plan upload failed for', file.name, err);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
    if (ok < files.length) alert(`Uploaded ${ok}/${files.length} plans. Some failed.`);
    loadPlans();
  };

  const adminAuth = adminToken
    ? { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true }
    : null;

  const patchPlan = async (id, patch) => {
    if (!adminAuth) return;
    try {
      await axios.put(`${API}/admin/technical-plans/${id}`, patch, adminAuth);
      loadPlans();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Update failed.');
    }
  };

  const handleRename = (plan, e) => {
    e.stopPropagation();
    const title = window.prompt('New title:', plan.title);
    if (!title || title === plan.title) return;
    patchPlan(plan.id, { title });
  };

  const handleDescription = (plan, e) => {
    e.stopPropagation();
    const description = window.prompt('Description / notes:', plan.description || '');
    if (description === null) return;
    patchPlan(plan.id, { description });
  };

  const handleChangeCategory = (plan, e) => {
    e.stopPropagation();
    const keys = Object.keys(CATEGORY_LABELS);
    const next = window.prompt(
      `Category (${keys.join(', ')}):`,
      plan.category || 'general'
    );
    if (!next || next === plan.category) return;
    patchPlan(plan.id, { category: next });
  };

  const handleDelete = async (plan, e) => {
    e.stopPropagation();
    if (!adminAuth) return;
    if (!window.confirm(`Delete "${plan.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/technical-plans/${plan.id}`, adminAuth);
      loadPlans();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Delete failed.');
    }
  };

  const categories = useMemo(() => {
    const set = new Set(plans.map((p) => p.category || 'general'));
    return ['all', ...Array.from(set)];
  }, [plans]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return plans.filter((p) => {
      if (filter !== 'all' && (p.category || 'general') !== filter) return false;
      if (q && !`${p.title} ${p.description || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [plans, filter, query]);

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent data-testid="technical-plans-dialog" className="w-[95vw] max-w-4xl max-h-[92vh] overflow-y-auto p-0" style={{ backgroundColor: '#F4F1EB' }}>
          <div className="p-5 sm:p-6">
            <DialogHeader className="mb-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <DialogTitle className="text-2xl sm:text-3xl flex items-center gap-2 flex-wrap" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>
                    <FileText className="w-6 h-6" style={{ color: '#1F4FBE' }} /> Property Technical Plans
                    <PresentationsButton deptId="property-plans" deptLabel="Property Technical Plans" />
                  </DialogTitle>
                  <p className="text-xs mt-1" style={{ fontFamily: 'Manrope', color: '#7A8C8C' }}>
                    Floor plans, architectural drawings, and engineering schematics for the reconstruction.
                  </p>
                </div>
                {adminToken && (
                  <>
                    <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.dwg,.dxf" className="hidden" onChange={handleUpload} />
                    <button
                      data-testid="plan-upload-btn"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50 flex-shrink-0"
                      style={{ backgroundColor: '#1F4FBE', fontFamily: 'Manrope' }}
                    >
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {uploading ? 'Uploading…' : 'Upload Plan'}
                    </button>
                  </>
                )}
              </div>
            </DialogHeader>

            {/* Toolbar */}
            <div className="flex gap-2 mb-4 flex-wrap">
              <div className="relative flex-1 min-w-[160px]">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#7A8C8C' }} />
                <input
                  data-testid="plans-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search plans…"
                  className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm outline-none"
                  style={{ borderColor: '#A3D4D4', fontFamily: 'Manrope', backgroundColor: 'white' }}
                />
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setFilter(c)}
                    data-testid={`plans-filter-${c}`}
                    className="px-3 py-1.5 rounded-full text-[10px] font-bold transition-all"
                    style={{
                      backgroundColor: filter === c ? (CATEGORY_COLORS[c] || '#1A2E2E') : '#EAE5D9',
                      color: filter === c ? 'white' : '#4A5D5D',
                      fontFamily: 'Manrope',
                    }}
                  >
                    {c === 'all' ? `All (${plans.length})` : `${CATEGORY_LABELS[c] || c} (${plans.filter((p) => (p.category || 'general') === c).length})`}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-12" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">
                  {plans.length === 0
                    ? (adminToken
                        ? 'No technical plans have been uploaded yet. Click "Upload Plan" above to add the first one.'
                        : 'No technical plans have been uploaded yet. Please check back soon.')
                    : 'No plans match your search or filter.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {filtered.map((p) => {
                  const ext = fileExt(p.file_name || p.file_url);
                  const color = CATEGORY_COLORS[p.category] || '#7A8C8C';
                  const cadThumb = thumbnailForCad(p.file_url);
                  const openPlan = () => (isImage(ext) ? setPreview(p) : window.open(p.file_url, '_blank'));
                  return (
                    <div
                      key={p.id}
                      data-testid={`plan-card-${p.id}`}
                      className="group relative text-left rounded-2xl overflow-hidden transition-all hover:-translate-y-1 hover:shadow-xl cursor-pointer"
                      style={{ backgroundColor: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}
                      onClick={openPlan}
                    >
                      <div className="aspect-[4/3] relative overflow-hidden" style={{ backgroundColor: '#EAE5D9' }}>
                        {isImage(ext) ? (
                          <img src={p.file_url} alt={p.title} className="w-full h-full object-cover" />
                        ) : cadThumb ? (
                          <img
                            src={cadThumb}
                            alt={`${p.title} preview`}
                            className="w-full h-full object-contain bg-white"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        {!isImage(ext) && (
                          <div
                            className="w-full h-full flex-col items-center justify-center gap-2"
                            style={{ display: cadThumb ? 'none' : 'flex', position: cadThumb ? 'absolute' : 'static', inset: 0 }}
                          >
                            {isPdf(ext) ? <FileText className="w-10 h-10" style={{ color }} /> : <ImageIcon className="w-10 h-10" style={{ color }} />}
                            <span className="text-[10px] font-bold uppercase" style={{ color, fontFamily: 'Manrope' }}>.{ext}</span>
                          </div>
                        )}
                        {(ext === 'dxf' || ext === 'dwg') && (
                          <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full text-[9px] font-bold text-white" style={{ backgroundColor: '#1A2E2E', fontFamily: 'Manrope' }}>
                            CAD · {ext.toUpperCase()}
                          </span>
                        )}
                        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9px] font-bold text-white" style={{ backgroundColor: color, fontFamily: 'Manrope' }}>
                          {CATEGORY_LABELS[p.category] || p.category || 'General'}
                        </span>
                        {adminToken && (
                          <div
                            className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              title="Rename"
                              onClick={(e) => handleRename(p, e)}
                              data-testid={`plan-rename-${p.id}`}
                              className="w-7 h-7 rounded-full flex items-center justify-center shadow-md transition-transform hover:scale-110"
                              style={{ backgroundColor: 'white', color: '#1F4FBE' }}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              title="Edit description"
                              onClick={(e) => handleDescription(p, e)}
                              data-testid={`plan-desc-${p.id}`}
                              className="w-7 h-7 rounded-full flex items-center justify-center shadow-md transition-transform hover:scale-110"
                              style={{ backgroundColor: 'white', color: '#1F4FBE' }}
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              title="Change category"
                              onClick={(e) => handleChangeCategory(p, e)}
                              data-testid={`plan-category-${p.id}`}
                              className="w-7 h-7 rounded-full flex items-center justify-center shadow-md transition-transform hover:scale-110"
                              style={{ backgroundColor: 'white', color: '#1F4FBE' }}
                            >
                              <Tag className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              title="Delete"
                              onClick={(e) => handleDelete(p, e)}
                              data-testid={`plan-delete-${p.id}`}
                              className="w-7 h-7 rounded-full flex items-center justify-center shadow-md transition-transform hover:scale-110"
                              style={{ backgroundColor: '#EF4444', color: 'white' }}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <p className="text-sm font-bold truncate" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>{p.title}</p>
                        {p.description && <p className="text-[10px] mt-0.5 line-clamp-2" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>{p.description}</p>}
                        <div className="flex items-center gap-2 mt-2 text-[10px]" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>
                          <span className="flex items-center gap-1 font-bold">{isImage(ext) ? 'View' : 'Open'} <ExternalLink className="w-3 h-3" /></span>
                          <span>·</span>
                          <a href={p.file_url} download onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 font-bold">
                            Download <Download className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Image preview lightbox (for images only) */}
      {preview && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/90 p-4"
          style={{ zIndex: 10003 }}
          onClick={() => setPreview(null)}
          data-testid="plan-preview"
        >
          <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center" onClick={() => setPreview(null)}>✕</button>
          <img src={preview.file_url} alt={preview.title} className="max-w-full max-h-full object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-xs font-bold text-white" style={{ backgroundColor: 'rgba(0,0,0,0.6)', fontFamily: 'Manrope' }}>
            {preview.title}
          </div>
        </div>
      )}
    </>
  );
}
